import React, { useState, useEffect, useCallback } from 'react';
import { timeApi, accountingApi, clientApi, projectApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Clock, Plus, Trash2, Pencil, Lightbulb, Timer, DollarSign, Sparkles, List, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import WeekTimesheet from '../components/WeekTimesheet';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const COLORS = ['#1e3a5f', '#2563eb', '#0891b2', '#d97706', '#7c3aed', '#64748b'];
const currentYear = new Date().getFullYear();

const BILLABLE_BY_DEFAULT = { 'Onsite Labor': true, 'Shop/Prep Work': true };

const emptyForm = () => ({
  activity_type: 'Onsite Labor',
  client_id: '',
  project_id: '',
  duration_hours: '',
  hourly_rate: '',
  is_billable: true,
  notes: '',
  date: new Date().toISOString().slice(0, 10),
});

export default function TimeTracking() {
  const [enabled, setEnabled] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [activityTypes, setActivityTypes] = useState([]);
  const [defaultRate, setDefaultRate] = useState(0);
  const [rateInput, setRateInput] = useState('');
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list');

  useEffect(() => {
    accountingApi.getStatus().then((r) => {
      setEnabled(!!r.data.enabled);
      setCanManage(!!r.data.can_manage);
    }).catch(() => setEnabled(false));
  }, []);

  const load = useCallback(async () => {
    try {
      const [s, e, sum, cl, pr] = await Promise.all([
        timeApi.getSettings(),
        timeApi.getEntries({ year: currentYear }),
        timeApi.getSummary(currentYear),
        clientApi.getAll(),
        projectApi.getAll(),
      ]);
      setActivityTypes(s.data.activity_types || []);
      setDefaultRate(s.data.default_hourly_rate || 0);
      setRateInput(String(s.data.default_hourly_rate || 0));
      setEntries(e.data || []);
      setSummary(sum.data);
      setClients(cl.data || []);
      setProjects(pr.data || []);
    } catch (err) {
      toast.error('Failed to load time data');
    }
  }, []);

  useEffect(() => { if (enabled) load(); }, [enabled, load]);

  const handleEnable = async () => {
    try {
      await accountingApi.enable(true);
      setEnabled(true);
      toast.success('Solo Builder bundle enabled');
    } catch { toast.error('Could not enable'); }
  };

  const onActivityChange = (v) => {
    setForm((f) => ({ ...f, activity_type: v, is_billable: BILLABLE_BY_DEFAULT[v] ?? false }));
  };

  const resetForm = () => { setForm(emptyForm()); setEditingId(null); };

  const startEdit = (e) => {
    setEditingId(e.id);
    setForm({
      activity_type: e.activity_type,
      client_id: e.client_id || '',
      project_id: e.project_id || '',
      duration_hours: String(e.duration_hours),
      hourly_rate: e.hourly_rate != null ? String(e.hourly_rate) : '',
      is_billable: e.is_billable,
      notes: e.notes || '',
      date: e.date,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveEntry = async () => {
    const hours = parseFloat(form.duration_hours);
    if (!(hours > 0)) { toast.error('Enter hours greater than 0'); return; }
    setSaving(true);
    const payload = {
      activity_type: form.activity_type,
      date: form.date,
      duration_hours: hours,
      client_id: form.client_id || null,
      project_id: form.project_id || null,
      hourly_rate: form.hourly_rate !== '' ? parseFloat(form.hourly_rate) : null,
      is_billable: form.is_billable,
      notes: form.notes.trim() || null,
    };
    try {
      if (editingId) {
        await timeApi.update(editingId, payload);
        toast.success('Time entry updated');
      } else {
        await timeApi.create(payload);
        toast.success('Time logged');
      }
      resetForm();
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to save time entry');
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this time entry?')) return;
    try { await timeApi.delete(id); load(); } catch { toast.error('Failed to delete'); }
  };

  const saveRate = async () => {
    try {
      await timeApi.updateSettings(parseFloat(rateInput) || 0);
      setDefaultRate(parseFloat(rateInput) || 0);
      toast.success('Default rate saved');
    } catch { toast.error('Failed to save rate'); }
  };

  if (enabled === null) return <div className="p-10 text-muted-foreground">Loading…</div>;

  if (!enabled) {
    return (
      <div className="max-w-2xl mx-auto py-10" data-testid="time-upgrade-card">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Timer className="h-5 w-5 text-blue-700" /> Time &amp; Activity Tracker</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Track billable and non-billable hours across jobs, proposals, shop work and supply runs —
              then pull unbilled time straight onto an invoice. Part of the <strong>Solo Builder bundle</strong>.
            </p>
            {canManage ? (
              <Button onClick={handleEnable} data-testid="enable-bundle-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Enable Solo Builder Bundle
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">Ask your account owner to enable the Solo Builder bundle.</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const chartData = (summary?.activity_breakdown || []).map((a, i) => ({ ...a, fill: COLORS[i % COLORS.length] }));

  return (
    <div className="space-y-6" data-testid="time-tracking-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Clock className="h-6 w-6 text-blue-700" /> Time Tracking</h1>
          <p className="text-muted-foreground">Log hours by activity — {currentYear}</p>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex rounded-md border overflow-hidden mr-1">
            <button
              className={`px-3 py-1.5 text-sm flex items-center gap-1 ${view === 'list' ? 'bg-blue-700 text-white' : 'bg-white text-slate-600'}`}
              onClick={() => setView('list')} data-testid="view-list-btn">
              <List className="h-4 w-4" /> List
            </button>
            <button
              className={`px-3 py-1.5 text-sm flex items-center gap-1 ${view === 'week' ? 'bg-blue-700 text-white' : 'bg-white text-slate-600'}`}
              onClick={() => setView('week')} data-testid="view-week-btn">
              <CalendarDays className="h-4 w-4" /> Week
            </button>
          </div>
          <div>
            <Label className="text-xs">Default $/hr</Label>
            <Input type="number" step="0.01" value={rateInput} onChange={(e) => setRateInput(e.target.value)}
              className="w-28 h-9" data-testid="default-rate-input" />
          </div>
          <Button variant="outline" onClick={saveRate} className="h-9" data-testid="save-rate-btn">Save Rate</Button>
        </div>
      </div>

      {/* Quick Log */}
      {view === 'list' && (
      <Card className="border-blue-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="h-4 w-4" /> {editingId ? 'Edit Time Entry' : 'Quick Log'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-3">
              <Label className="text-xs">Activity</Label>
              <Select value={form.activity_type} onValueChange={onActivityChange}>
                <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(activityTypes.length ? activityTypes : Object.keys(BILLABLE_BY_DEFAULT)).map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Client (optional)</Label>
              <Select value={form.client_id || 'none'} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="time-client-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Project (optional)</Label>
              <Select value={form.project_id || 'none'} onValueChange={(v) => setForm((f) => ({ ...f, project_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="time-project-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs">Hours</Label>
              <Input type="number" step="0.25" value={form.duration_hours}
                onChange={(e) => setForm((f) => ({ ...f, duration_hours: e.target.value }))} data-testid="time-hours-input" />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="time-date-input" />
            </div>
            <div className="md:col-span-1 flex flex-col items-center">
              <Label className="text-xs">Billable</Label>
              <Switch checked={form.is_billable} onCheckedChange={(c) => setForm((f) => ({ ...f, is_billable: c }))} data-testid="time-billable-switch" className="mt-2" />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button onClick={saveEntry} disabled={saving} className="flex-1" data-testid="save-time-btn">
                {editingId ? 'Update' : 'Log Time'}
              </Button>
              {editingId && <Button variant="outline" onClick={resetForm}>Cancel</Button>}
            </div>
            <div className="md:col-span-12">
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. Researched local structural codes for deck load" data-testid="time-notes-input" />
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard label="Total Hours" value={`${summary?.total_hours ?? 0} hrs`} icon={Clock} />
        <SummaryCard label="Billable Hours" value={`${summary?.billable_hours ?? 0} hrs`} icon={Timer} tint="text-green-700" />
        <SummaryCard label="Non-Billable" value={`${summary?.nonbillable_hours ?? 0} hrs`} icon={Timer} tint="text-slate-600" />
        <SummaryCard label="Unbilled Value" value={fmt(summary?.unbilled_billable_value)} icon={DollarSign} tint="text-blue-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity breakdown chart */}
        <Card>
          <CardHeader><CardTitle className="text-base">Activity Breakdown</CardTitle></CardHeader>
          <CardContent>
            {chartData.length === 0 ? (
              <p className="text-muted-foreground text-center py-10">No time logged yet this year.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={chartData} dataKey="hours" nameKey="activity_type" cx="50%" cy="50%" outerRadius={90} innerRadius={50} label={(d) => `${d.hours}h`}>
                    {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `${v} hrs`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Unbilled research warning */}
        <Card className="bg-amber-50 border-amber-200">
          <CardHeader><CardTitle className="text-base flex items-center gap-2 text-amber-800"><Lightbulb className="h-4 w-4" /> Unbilled Research &amp; Proposal Time</CardTitle></CardHeader>
          <CardContent>
            {(summary?.unbilled_research || []).length === 0 ? (
              <p className="text-amber-800/80 text-sm py-6">No unbilled pre-bid research logged. Nice — or log some as you research bids!</p>
            ) : (
              <div className="space-y-3" data-testid="unbilled-research">
                <p className="text-sm text-amber-900">
                  You spent <strong>{summary.unbilled_research_total} hours</strong> on pre-bid research this year.
                  Attach it to an invoice or use it to refine future bids.
                </p>
                {summary.unbilled_research.map((r) => (
                  <div key={r.client} className="flex justify-between text-sm border-b border-amber-200 pb-1">
                    <span className="text-amber-900">{r.client}</span>
                    <span className="font-medium text-amber-900">{r.hours} hrs</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Entries: list or week grid */}
      <Card>
        <CardHeader><CardTitle className="text-base">{view === 'week' ? 'Weekly Timesheet' : 'Time Entries'}</CardTitle></CardHeader>
        <CardContent>
          {view === 'week' ? (
            <WeekTimesheet activityTypes={activityTypes} onChanged={load} />
          ) : entries.length === 0 ? (
            <p className="text-muted-foreground text-center py-6" data-testid="no-time-entries">No time entries yet.</p>
          ) : (
            <div className="divide-y">
              {entries.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-3 py-3 flex-wrap" data-testid={`time-entry-${e.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{e.activity_type}</span>
                      <Badge variant={e.is_billable ? 'default' : 'secondary'} className="text-xs">{e.is_billable ? 'Billable' : 'Non-billable'}</Badge>
                      {e.is_billed && <Badge className="bg-green-600 text-xs">Billed</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {e.date} · {e.duration_hours} hrs
                      {e.hourly_rate ? ` @ ${fmt(e.hourly_rate)}` : ''}
                      {e.project_name ? ` · ${e.project_name}` : e.client_name ? ` · ${e.client_name}` : ''}
                      {e.notes ? ` — ${e.notes}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {e.is_billable && e.hourly_rate ? <span className="text-sm font-medium tabular-nums">{fmt(e.duration_hours * e.hourly_rate)}</span> : null}
                    <Button variant="ghost" size="sm" onClick={() => startEdit(e)} data-testid={`edit-time-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => del(e.id)} disabled={e.is_billed} data-testid={`delete-time-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const SummaryCard = ({ label, value, icon: Icon, tint = 'text-foreground' }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className={`text-xl font-bold ${tint}`}>{value}</p>
        </div>
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
    </CardContent>
  </Card>
);
